import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Editor } from "@tinymce/tinymce-react";
import { Editor as TinyMCEEditor } from "tinymce";
import {
  Button,
  Modal,
  Form,
  Input,
  Card,
  Row,
  Col,
  Select,
  message,
} from "antd";
import type { SelectProps } from "antd";
import { deepKeys } from "deeks";

import {
  getDocumentApi,
  addGroupDocumentApi,
  addCommunicationDocumentApi,
  addImageApi,
  editDocumentApi,
} from "../../services/Document/DocumentApis";
import { getCompanyMembersApi } from "../../services/Member/MemberApis";
import { useEditorContext } from "../../hooks/useEditorContext";
import { useUnsavedChangesGuard } from "../../hooks/useUnsavedChangesGuard";
import { rememberCommunicationReturn } from "../../utils/groupTabStorage";

interface WysiWigProps {
  groupId: any;
  type: string;
}

type CustomVariable = {
  id?: number;
  Name: string;
  Value: string;
};

let options: SelectProps["options"] = [];

const DEFAULT_VARIABLES: CustomVariable[] = [
  { id: 0, Name: "Covered Live Table", Value: "CoveredLiveTable" },
  { id: 1, Name: "Addon Table", Value: "AddonTable" },
  { id: 2, Name: "Policy Number", Value: "Policy.PolicyNumber" },
  { id: 3, Name: "Policy Holder Name", Value: "Name" },
  { id: 4, Name: "Policy Holder Surname", Value: "Surname" },
  { id: 5, Name: "Today's Date", Value: "Date" },
  { id: 6, Name: "Inception Date", Value: "InceptionDate" },
  { id: 7, Name: "Premium", Value: "Policy.Product.PolicyPremium" },
  { id: 8, Name: "Minimum Age", Value: "Policy.Product.ProductCategory.MinAge" },
  { id: 9, Name: "Maximum Age", Value: "Policy.Product.ProductCategory.MaxAge" },
  { id: 10, Name: "Cover Amount", Value: "Policy.Product.CoverAmount" },
  { id: 11, Name: "Addon Premium", Value: "AddonPremium" },
  { id: 12, Name: "Total Premium", Value: "TotalPremium" },
  { id: 13, Name: "Plan Type", Value: "Policy.Product.PlanCategory.Name" },
  { id: 14, Name: "Policy Holder Signature", Value: "Signature" },
  { id: 15, Name: "Product Name", Value: "Policy.Product.ProductName" },
  { id: 16, Name: "Debit Day", Value: "PaymentType.PaymentDate" },
  { id: 17, Name: "Payment Type", Value: "PaymentType.Type" },
  { id: 18, Name: "NuPay Debit Day", Value: "PaymentType.PaymentDate" },
  { id: 19, Name: "Beneficiary Name", Value: "Beneficiary.BenName" },
  { id: 20, Name: "Beneficiary Surname", Value: "Beneficiary.BenSurname" },
  { id: 21, Name: "Relationship", Value: "Beneficiary.BenRelationship" },
];

/** Leaf paths that must stay selectable even when the sample member has no beneficiary. */
const BENEFICIARY_VARIABLE_PATHS = [
  "Beneficiary.BenName",
  "Beneficiary.BenSurname",
  "Beneficiary.BenRelationship",
];

/**
 * Whole-expression legacy paths. Beneficiary.BenName is not a key, so nested
 * paths are left alone.
 */
const LEGACY_BENEFICIARY_EJS: Record<string, string> = {
  BenName: "Beneficiary.BenName",
  BenSurname: "Beneficiary.BenSurname",
  BenRelationship: "Beneficiary.BenRelationship",
};

/** Display tokens that must become beneficiary leaves, regardless of casing. */
const BENEFICIARY_DISPLAY_ALIASES: Record<string, string> = {
  "beneficiary name": "Beneficiary.BenName",
  "beneficiary surname": "Beneficiary.BenSurname",
  "beneficiary relationship": "Beneficiary.BenRelationship",
  relationship: "Beneficiary.BenRelationship",
};

/** Object roots that must not be used as scalar EJS Values (renders as [object Object]). */
const NON_SCALAR_VARIABLE_VALUES = new Set([
  "PaymentType",
  "SecondaryPaymentType",
  "Beneficiary",
]);

const serializeVariables = (variables: CustomVariable[]) =>
  JSON.stringify(
    variables.map((variable) => ({
      Name: variable.Name,
      Value: variable.Value,
    }))
  );

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const isCoveredLiveTable = (variable: CustomVariable) =>
  variable.Name === "CoveredLiveTable" ||
  variable.Value === "CoveredLiveTable" ||
  variable.Name === "Covered Live Table";

const isNonScalarVariableValue = (value: string) =>
  NON_SCALAR_VARIABLE_VALUES.has(String(value).trim());

const rewriteLegacyBeneficiaryExpr = (expr: string) =>
  LEGACY_BENEFICIARY_EJS[expr.trim()] ?? expr.trim();

const normalizeTokenName = (raw: string) =>
  raw
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const beneficiaryPathForToken = (name: string) =>
  BENEFICIARY_DISPLAY_ALIASES[normalizeTokenName(name)];

const nonScalarPathMessage = (valuePath: string) => {
  if (valuePath === "Beneficiary") {
    return `Use Beneficiary.BenName, Beneficiary.BenSurname, or Beneficiary.BenRelationship — "${valuePath}" is an object and renders as [object Object].`;
  }
  return `Use a leaf path such as ${valuePath}.Type or ${valuePath}.PaymentDate — "${valuePath}" is an object and renders as [object Object].`;
};

/** Rewrite <%= BenName %> (and encoded forms) without touching Beneficiary.BenName. */
const rewriteLegacyBeneficiaryEjs = (html: string) =>
  html.replace(
    /(?:&lt;|<)%=?\s*([^%]+?)\s*%(?:>|&gt;)/g,
    (full, expr: string) => {
      const key = String(expr).trim();
      const rewritten = LEGACY_BENEFICIARY_EJS[key];
      if (!rewritten) return full;
      if (full.includes("&lt;")) {
        return `&lt;%= ${rewritten} %&gt;`;
      }
      return `<%= ${rewritten} %>`;
    }
  );

const normalizeVariable = (variable: any): CustomVariable | null => {
  const Name = variable?.Name ?? variable?.name;
  const Value = variable?.Value ?? variable?.value;
  if (!Name) return null;
  return {
    ...variable,
    Name: String(Name),
    Value: Value != null ? String(Value) : "",
  };
};

/** Keep saved Variables; append any DEFAULT_VARIABLES missing by Name (e.g. Debit Day on older templates). */
const mergeDefaultVariables = (
  saved: CustomVariable[]
): CustomVariable[] => {
  if (!saved.length) return [...DEFAULT_VARIABLES];

  const byName = new Map(
    saved.map((variable) => [variable.Name.trim().toLowerCase(), variable])
  );

  const merged = saved.map((variable) => {
    const nameKey = variable.Name.trim().toLowerCase();
    const valuePath = variable.Value.trim();
    const legacyBeneficiary = LEGACY_BENEFICIARY_EJS[valuePath];
    if (legacyBeneficiary) {
      return { ...variable, Value: legacyBeneficiary };
    }
    const aliasPath = beneficiaryPathForToken(variable.Name);
    if (
      aliasPath &&
      (isNonScalarVariableValue(valuePath) ||
        valuePath === "" ||
        normalizeTokenName(valuePath) === nameKey)
    ) {
      return { ...variable, Value: aliasPath };
    }
    // Upgrade legacy object roots so generate/editor use the leaf path.
    if (isNonScalarVariableValue(valuePath) && nameKey === "payment type") {
      return { ...variable, Value: "PaymentType.Type" };
    }
    return variable;
  });

  let nextId =
    merged.reduce(
      (max, variable) =>
        typeof variable.id === "number" && variable.id > max
          ? variable.id
          : max,
      DEFAULT_VARIABLES.length
    ) + 1;

  DEFAULT_VARIABLES.forEach((defaultVar) => {
    const key = defaultVar.Name.trim().toLowerCase();
    if (byName.has(key)) return;
    merged.push({ ...defaultVar, id: nextId++ });
    byName.set(key, defaultVar);
  });

  return merged;
};

/** Convert display tokens {{Name}} → EJS for persistence. */
const toEjsHtml = (html: string, variables: CustomVariable[]) => {
  let ejsContent = html.replace(
    /\{\{([\s\S]*?)\}\}/g,
    (full, inner: string) => {
      const alias = beneficiaryPathForToken(inner);
      if (alias) return `<%= ${alias} %>`;

      const key = normalizeTokenName(inner);
      const variable = variables.find(
        (item) => normalizeTokenName(item.Name) === key && item.Value
      );
      if (!variable) return full;
      const value = rewriteLegacyBeneficiaryExpr(variable.Value);
      if (isNonScalarVariableValue(value)) return full;
      return `<%= ${value} %>`;
    }
  );

  variables.forEach((variable) => {
    const regex = new RegExp(`{{${escapeRegExp(variable.Name)}}}`, "g");
    ejsContent = ejsContent.replace(regex, `<%= ${variable.Value} %>`);
  });

  return rewriteLegacyBeneficiaryEjs(ejsContent);
};

/**
 * Convert stored EJS back to {{Name}} for editing.
 * TinyMCE often persists angle brackets as &lt;%= … %&gt;, so match both forms.
 * Legacy templates may still store bare CoveredLiveTable (no EJS tags).
 */
const toDisplayHtml = (html: string, variables: CustomVariable[]) => {
  let display = html ?? "";

  const lookup = new Map<string, string>();
  [...DEFAULT_VARIABLES, ...variables].forEach((variable) => {
    const normalized = normalizeVariable(variable);
    if (!normalized?.Name || !normalized.Value) return;
    lookup.set(normalized.Value.trim(), normalized.Name);
  });

  // Resolve EJS first so bare-key legacy replace does not corrupt <%= CoveredLiveTable %>.
  display = display.replace(
    /(?:&lt;|<)%=?\s*([^%]+?)\s*%(?:>|&gt;)/g,
    (full, expr: string) => {
      const raw = String(expr).trim();
      const key = rewriteLegacyBeneficiaryExpr(raw);
      const name = lookup.get(key);
      if (name) return `{{${name}}}`;
      if (key !== raw) {
        return full.includes("&lt;")
          ? `&lt;%= ${key} %&gt;`
          : `<%= ${key} %>`;
      }
      return full;
    }
  );

  [...DEFAULT_VARIABLES, ...variables].forEach((variable) => {
    const normalized = normalizeVariable(variable);
    if (!normalized || !isCoveredLiveTable(normalized) || !normalized.Value) {
      return;
    }
    if (display.includes(normalized.Value)) {
      display = display
        .split(normalized.Value)
        .join(`{{${normalized.Name}}}`);
    }
  });

  return display;
};

const WysiWigEditor: React.FC<WysiWigProps> = ({ groupId, type }) => {
  const navigate = useNavigate();
  const { editorContent, editorUpdate } = useEditorContext();
  const [content, setContent] = useState(() =>
    toDisplayHtml(editorContent?.Html ?? "", DEFAULT_VARIABLES)
  );
  const [customVariables, setCustomVariables] = useState<CustomVariable[]>([]);
  const [variableModalVisible, setVariableModalVisible] = useState(false);
  const [documentModalVisible, setDocumentModalVisible] = useState(false);
  const [lastVariableId, setLastVariableId] = useState<number>(10);
  const [loading, setLoading] = useState(false);
  const [baselineReady, setBaselineReady] = useState(false);

  const [variableForm] = Form.useForm();
  const [documentForm] = Form.useForm();
  const editorRef = useRef<TinyMCEEditor | null>(null);
  const baselineRef = useRef<{ content: string; variables: string } | null>(
    null
  );

  const isAdminPath = window.location.pathname.includes("/admin/");

  const isDirty = useMemo(() => {
    if (!baselineReady || !baselineRef.current) {
      return false;
    }

    return (
      content !== baselineRef.current.content ||
      serializeVariables(customVariables) !== baselineRef.current.variables
    );
  }, [baselineReady, content, customVariables]);

  const { markSaved } = useUnsavedChangesGuard({
    id: `wysiwyg-editor-${groupId}-${type}`,
    when: isDirty,
    message:
      "You have unsaved template changes. Are you sure you want to leave?",
  });

  const navigateBackToGroup = () => {
    rememberCommunicationReturn(groupId, type, isAdminPath);
    const base = isAdminPath
      ? `/admin/groups/${groupId}`
      : `/groups/${groupId}`;
    navigate(base);
  };

  const fetchVariables = async (): Promise<CustomVariable[] | undefined> => {
    const member = await getCompanyMembersApi(groupId);

    const values = deepKeys(member.data[0], {
      expandArrayObjects: true,
      ignoreEmptyArraysWhenExpanding: true,
    });

    const filteredList = values.filter(
      (item) =>
        !item.startsWith("User") &&
        !item.startsWith("Document") &&
        !item.startsWith("CoveredLive") &&
        !item.startsWith("Policy") &&
        !item.startsWith("AuditTrail") &&
        !item.startsWith("CommunicationDocument") &&
        !isNonScalarVariableValue(item)
    );
    const optionPaths = [...filteredList];
    BENEFICIARY_VARIABLE_PATHS.forEach((path) => {
      if (!optionPaths.includes(path)) optionPaths.push(path);
    });
    options = optionPaths.map((value: string) => {
      return {
        value: value,
        label: value,
      };
    });

    if (!editorUpdate) {
      return DEFAULT_VARIABLES;
    }

    return undefined;
  };

  const fetchDocument = async (): Promise<{
    variables: CustomVariable[];
    html: string;
  }> => {
    const { data } = await getDocumentApi(editorContent.id, editorContent.Type);
    const updatedCustomVariables: CustomVariable[] = [];

    if (data && Object.keys(data).length > 0) {
      const rawVariables = data.Variable || data.Variables || [];
      (Array.isArray(rawVariables) ? rawVariables : []).forEach(
        (variable: any) => {
          const normalized = normalizeVariable(variable);
          if (normalized) updatedCustomVariables.push(normalized);
        }
      );
    }

    const rawHtml = data?.Html ?? editorContent?.Html ?? "";
    const variables = mergeDefaultVariables(updatedCustomVariables);

    return {
      variables,
      html: toDisplayHtml(rawHtml, variables),
    };
  };

  const captureBaseline = (
    nextContent: string,
    nextVariables: CustomVariable[]
  ) => {
    baselineRef.current = {
      content: nextContent ?? "",
      variables: serializeVariables(nextVariables),
    };
    setBaselineReady(true);
  };

  const handleAddDocument = async (values: any) => {
    setLoading(true);

    const ejsContent = toEjsHtml(content, customVariables);

    return await addGroupDocumentApi(groupId, {
      Name: values.Name,
      Html: ejsContent,
      Variables: customVariables,
    })
      .then((res) => {
        markSaved();
        navigateBackToGroup();
        setLoading(false);
        return res;
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
      });
  };

  const handleAddCommunicationDocument = async (values: any) => {
    setLoading(true);
    const ejsContent = toEjsHtml(content, customVariables);

    return await addCommunicationDocumentApi(groupId, {
      Name: values.Name,
      Html: ejsContent,
      Variables: customVariables,
    })
      .then((res) => {
        markSaved();
        navigateBackToGroup();
        setLoading(false);
        return res;
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
      });
  };

  const handleEditDocument = async (values: any) => {
    setLoading(true);
    const ejsContent = toEjsHtml(content, customVariables);

    return await editDocumentApi(editorContent.id, editorContent.Type, {
      Name: values.Name,
      Html: ejsContent,
      Variables: customVariables,
    })
      .then((res) => {
        markSaved();
        navigateBackToGroup();
        setLoading(false);
        return res;
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
      });
  };

  const handleEditorChange = (nextContent: string) => {
    setContent(nextContent);
  };

  const handleAddCustomVariable = (variable: CustomVariable) => {
    const variableId = `{{${variable.Name}}}`;
    if (editorRef.current != null) {
      editorRef.current.insertContent(variableId);
    }
  };

  const handleOkVariableButtonClick = () => {
    variableForm
      .validateFields()
      .then((values) => {
        const valuePath = String(values?.Value ?? "").trim();
        if (isNonScalarVariableValue(valuePath)) {
          message.error(nonScalarPathMessage(valuePath));
          return;
        }

        setCustomVariables((prev) => [
          ...prev,
          {
            ...values,
            id: lastVariableId + 1,
            Name: String(values.Name ?? "").trim(),
            Value: valuePath,
          },
        ]);

        setVariableModalVisible(false);
        setLastVariableId((prevId) => prevId + 1);
        variableForm.resetFields();
      })
      .catch((errorInfo) => {
        console.log("Validation failed:", errorInfo);
      });
  };
  const handleOkDocumentButtonClick = () => {
    documentForm
      .validateFields()
      .then((values) => {
        if (type === "Communication") {
          if (editorUpdate) {
            handleEditDocument(values);
          } else {
            handleAddCommunicationDocument(values);
          }
        } else if (editorUpdate) {
          handleEditDocument(values);
        } else {
          handleAddDocument(values);
        }
      })
      .catch((errorInfo) => {
        console.log("Validation failed:", errorInfo);
      });
  };

  const handleFilePicker = (
    callback: (blobInfo: any) => void,
    _value: any,
    _meta: any
  ) => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");

    input.addEventListener("change", (e) => {
      if (!e.target) return;
      const fileInput = e.target as HTMLInputElement;
      if (!fileInput.files) return;

      const file = fileInput.files[0];

      if (!file) return;

      const reader = new FileReader();

      reader.onload = async () => {
        const base64 = reader.result;

        if (base64) {
          const response = await addImageApi({ base64: base64 });
          if (!response.success) {
            message.error("Image size exceeds the limit (1MB).");
          }
          callback(response.data);
        } else {
          message.error("Incorrect file type");
        }
      };

      reader.onerror = () => {
        console.error("Failed to read the file");
        message.error("Failed to read the file");
      };

      if (file) {
        reader.readAsDataURL(file);
      }
    });

    input.click();
  };

  useEffect(() => {
    const loadEditor = async () => {
      let initialContent = toDisplayHtml(
        editorContent?.Html ?? "",
        DEFAULT_VARIABLES
      );
      const defaultVariables = await fetchVariables();
      let nextVariables = customVariables;

      if (editorUpdate) {
        const doc = await fetchDocument();
        nextVariables = doc.variables;
        initialContent = doc.html;
      } else if (defaultVariables) {
        nextVariables = defaultVariables;
      }

      setContent(initialContent);
      setCustomVariables(nextVariables);
      captureBaseline(initialContent, nextVariables);
    };

    void loadEditor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <div className="interactions">
        <Button size="large" onClick={() => setVariableModalVisible(true)}>
          Add Custom Variable
        </Button>
        {editorUpdate ? (
          <Button onClick={() => setDocumentModalVisible(true)} size="large">
            Save
          </Button>
        ) : (
          <Button onClick={() => setDocumentModalVisible(true)} size="large">
            Add
          </Button>
        )}
      </div>

      <Row>
        <Col span={4}>
          <h4 style={{ textAlign: "center" }}>Variables</h4>
          <div style={{ margin: "24px 0" }}>
            <Row
              gutter={[4, 4]}
              style={{
                marginRight: "16px",
                height: "600px",
                overflowY: "scroll",
              }}
            >
              {customVariables.map((variable: CustomVariable) => (
                <Col key={variable.id} span={22}>
                  <Card
                    hoverable={true}
                    onClick={() => handleAddCustomVariable(variable)}
                  >
                    {variable.Name}
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </Col>
        <Col span={20}>
          <Editor
            apiKey="u0aupber54flf7tst82ia4stvdjne52u4cl5febngtp3qo4v"
            value={content}
            onInit={(evt, editor) => (editorRef.current = editor)}
            onEditorChange={handleEditorChange}
            init={{
              height: 700,
              plugins:
                "view advlist visualblocks visualchar autolink lists link image table charmap preview pagebreak wordcount anchor",
              toolbar:
                "print | pagebreak | undo redo | blocks fontfamily fontsize forecolor backcolor | formatselect | bold italic backcolor | \
            alignleft aligncenter alignright alignjustify | \
            bullist numlist outdent indent | removeformat | \
            image table",
              pagebreak_separator:
                "<div style='page-break-before: always; clear:both'></div>",
              file_picker_types: "image",
              file_picker_callback: handleFilePicker,
              automatic_uploads: true,
              content_style: `
            body {
              background: #fff;
            }

            @media (min-width: 840px) {
            html {
              background: #eceef4;
              min-height: 100%;
              padding: 0 .5rem;
            }

            body {
              background-color: #fff;
              box-shadow: 0 0 4px rgba(0, 0, 0, .15);
              box-sizing: border-box;
              margin: 1rem auto 0;
              max-width: 820px;
              min-height: calc(100vh - 1rem);
              padding:4rem 6rem 6rem 6rem;
              }
            }
        `,
            }}
          />
        </Col>
      </Row>

      <Modal
        title="Add Variable"
        open={variableModalVisible}
        onCancel={() => setVariableModalVisible(false)}
        onOk={handleOkVariableButtonClick}
      >
        <Form form={variableForm} layout="vertical">
          <Form.Item
            label="Variable Name"
            name="Name"
            rules={[{ required: true, message: "Please enter a variable name." }]}
          >
            <Input size="large" />
          </Form.Item>
          <Form.Item
            label="Variable Value"
            name="Value"
            rules={[
              { required: true, message: "Please select a value path." },
              {
                validator: async (_, value) => {
                  if (value != null && isNonScalarVariableValue(String(value))) {
                    return Promise.reject(
                      new Error(nonScalarPathMessage(String(value).trim()))
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Select
              showSearch
              style={{ width: "100%" }}
              size="large"
              placeholder="Select Value"
              options={options}
              filterOption={(input, option) =>
                (option?.label as string)
                  ?.toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
        </Form>
      </Modal>

      {type === "Document" ? (
        <Modal
          title={!editorUpdate ? "Add Document" : "Edit Document"}
          open={documentModalVisible}
          onCancel={() => setDocumentModalVisible(false)}
          onOk={handleOkDocumentButtonClick}
          confirmLoading={loading}
        >
          <Form form={documentForm} layout="vertical">
            <Form.Item
              label="Document Name"
              initialValue={editorContent.Name}
              name="Name"
              rules={[
                { required: true, message: "Please enter the Document name." },
              ]}
            >
              <Input />
            </Form.Item>
          </Form>
        </Modal>
      ) : (
        <Modal
          title={
            !editorUpdate
              ? "Add Communication Document"
              : "Edit Communication Document"
          }
          open={documentModalVisible}
          onCancel={() => setDocumentModalVisible(false)}
          onOk={handleOkDocumentButtonClick}
          confirmLoading={loading}
        >
          <Form form={documentForm} layout="vertical">
            <Form.Item
              label="Document Name"
              initialValue={editorContent.Name}
              name="Name"
              rules={[
                { required: true, message: "Please enter the Document name." },
              ]}
            >
              <Input />
            </Form.Item>
          </Form>
        </Modal>
      )}
    </div>
  );
};

export default WysiWigEditor;
